/* src/components/pages/Extraerlibrocomprasenr.jsx - Modificado el 25-01-2025 */
/* -------- scraping para libro de compras mensual clientes on demand ------- */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';

import {
  TextField,
  Box,
  Button,
  Stack,
  Typography,
  MenuItem,
  LinearProgress
} from '@mui/material';

import api from '../../services/api.js'; // Api Interceptor en vez de Axios

import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import { CustomToolbar } from "../../components/datagrid/CustomToolbar";
import DataGridSkeleton from "../../components/datagrid/DataGridSkeleton";

import { normalizeRut } from "../utils/rutUtils";
import { validarRut } from "../utils/rutValidator";

import { RiFileSearchLine } from "react-icons/ri";

import { useAuth } from "../../context/AuthContext"; // token

import { useFeedback } from '../../context/FeedbackContext'; // Mensajes breves


const Extraerlibrocomprasenr = ({ modo }) => {
  const { user } = useAuth();

  /* ===============================
    Estados principales
  =============================== */

  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(false);

  /* Enrolados asociados */
  const [enrolados, setEnrolados] = useState([]);
  const [rutSeleccionado, setRutSeleccionado] = useState("");

  /* Async Jobs */
  const [jobId, setJobId] = useState(null);

  /* Job trabajando */
  const scraperOcupado = jobId !== null;

  /* Progreso Async */
  const [progress, setProgress] = useState(null);
  const [progresoTexto, setProgresoTexto] = useState("");

  /* Periodo */
  const [ano, setAno] = useState(new Date().getFullYear()); /* Inicializa con el año actual */
  const [mes, setMes] = useState(1); /* Inicia con mes 1 enero */

  /* Estados para paginación desde el servidor */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* Dialogo y confirmación */
  const { showFeedback } = useFeedback(); // Mensajes breves no bloqueantes

  /* Cálculo dinámico de los años */
  const currentYear = new Date().getFullYear(); // Año actual
  const years = Array.from({ length: 7 }, (_, index) => currentYear - index);

  /* Manejo del cambio del año */
  const handleAnoChange = (e) => {
    setAno(e.target.value);
  };

  /* Manejo del cambio del mes */
  const handleMesChange = (e) => {
    setMes(e.target.value);
  };

  /* ===============================
     Validación Rut Cliente
  =============================== */

  const rutValido = useMemo(() => {
    if (!user?.usuario) return false;
    return validarRut(user.usuario);
  }, [user?.usuario]);

  /* ===============================
     Validación Rut Enrolado
  =============================== */

  const rutEnrValido = useMemo(() => {
    if (!rutSeleccionado) return false;
    return validarRut(rutSeleccionado);
  }, [rutSeleccionado]);

  /* ===============================
     Cargar enrolados asociados
  =============================== */

  useEffect(() => {
    if (!user?.usuario) return;

    (async () => {
      try {
        const RutCli = normalizeRut(user.usuario);

        const { data } = await api.post("/enrolados/enroladosasociados", {
          RutCli,
          RutEnr: "",
          Tipousr: 1,
        });

        if (data?.success && data?.rows?.length) {
          setEnrolados(data.rows);
        } else {
          setEnrolados([]);
          showFeedback({
            message: "No se encontraron enrolados asociados",
            severity: "warning",
          });
        }
      } catch {
        showFeedback({
          message: "No fue posible cargar enrolados",
          severity: "error",
        });
      }
    })();
  }, [user?.usuario, showFeedback]);


  /* ===============================
     Bitácora server-side
  =============================== */

  const fetchBitacora = useCallback(async () => {
    if (!rutValido) {
      showFeedback({
        message: 'El Rut ingresado no es válido',
        severity: 'error',
      });
      setBitacora([]);
      setRowCount(0);
      return;
    }

    setLoading(true);

    try {
      const RutCli = normalizeRut(user.usuario);

      const { data } = await api.post('/librocompras/getbitacora', {
        RutCli,
        page,
        pageSize,
      });

      if (data?.success) {
        setBitacora(data.rows || []);
        setRowCount(data.total || 0);
      } else {
        setBitacora([]);
        setRowCount(0);
      }
    } catch {
      setBitacora([]);
      setRowCount(0);
      showFeedback({
        message: 'No fue posible obtener la bitácora',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [rutValido, user.usuario, page, pageSize, showFeedback]);


  /* llamar a cargar bitacora */
  useEffect(() => {
    fetchBitacora();
  }, [fetchBitacora]);

  /* ===============================
     Polling Async Scraping Job
  =============================== */
  useEffect(() => {
    if (!jobId) return;

    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/extraerlibrocompras/status/${jobId}`
        );

        /* Progreso live */
        if (data.progress && typeof data.progress === "object") {
          setProgress(data.progress);

          if (data.progress.message) {
            setProgresoTexto(data.progress.message);
          }
        }

        if (data.state === "active") {
          setProgresoTexto((prev) =>
            prev ? prev : "⏳ Iniciando extracción..."
          );
        }

        /* Completed */
        if (data.state === "completed") {
          clearInterval(interval);

          setProgresoTexto("🎉 Extracción completada");

          showFeedback({
            message: "✅ Extracción LibroCompras finalizada",
            severity: "success",
          });

          await fetchBitacora();

          setTimeout(() => {
            setProgress(null);
            setProgresoTexto("");
          }, 4000);

          setJobId(null);
          return;
        }

        /* Failed */
        if (data.state === "failed") {
          clearInterval(interval);

          setProgress(null);
          setProgresoTexto("");
          setJobId(null);

          showFeedback({
            message: "❌ Falló el scraping LibroCompras",
            severity: "error",
          });

          return;
        }
      } catch {
        clearInterval(interval);

        setProgress(null);
        setProgresoTexto("");
        setJobId(null);

        showFeedback({
          message: "❌ No fue posible consultar estado del Job",
          severity: "error",
        });
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [jobId, fetchBitacora, showFeedback]);


  /* ===============================
     Solicitar scraping async
  =============================== */

  const solicitarExtraccion = async () => {
    if (!rutValido) {
      showFeedback({
        message: "El Rut del cliente no es válido",
        severity: "error",
      });
      return;
    }

    if (!rutEnrValido) {
      showFeedback({
        message: "Debe seleccionar un enrolado válido",
        severity: "error",
      });
      return;
    }

    try {
      const RutCli = normalizeRut(user.usuario);

      showFeedback({
        message: "⏳ Iniciando scraping LibroCompras...",
        severity: "info",
      });

      const { data } = await api.post("/extraerlibrocompras/start", {
        RutCli,
        RutEnr: "",
        TipCli: 1,
        Tipoing: 1,
        Mesaconsultar: mes,
        Anoaconsultar: ano,
      });

      if (data?.success) {
        setProgress(null);
        setProgresoTexto("");
        setJobId(data.jobId);

        showFeedback({
          message: "📌 Proceso ejecutándose en segundo plano",
          severity: "info",
        });
      } else {
        showFeedback({
          message: data?.message || "No fue posible iniciar extracción",
          severity: "error",
        });
      }
    } catch {
      showFeedback({
        message: "❌ Error al iniciar scraping LibroCompras",
        severity: "error",
      });
    }
  };

 
  /* ===============================
     DataGrid Config
  =============================== */

  const columns = React.useMemo(() => ([
    {
      field: 'id',
      headerName: 'Id Registro',
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: "fecha",
      headerName: "Fecha",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "—";
        return new Date(params.value).toLocaleString("es-CL");
      },
    },
    {
      field: 'tipo',
      headerName: "Tipo",
      width: 100,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'rutcli',
      headerName: "Rut Cliente",
      width: 130,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'rutenr',
      headerName: "Rut Enrolado",
      width: 200,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: "nombre",
      headerName: "Razon Social",
      width: 300,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "detalle",
      headerName: "Detalle",
      flex: 1,
      minWidth: 400,
    },
  ]), []);


  /* ===============================
     Render
  =============================== */
  return (
    <Box sx={{ width: '100%', pt: 0, overflow: 'visible' }}>
      {/* Barra de Controles */}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{
          mb: 1,
          flexShrink: 0,       // 👈 NO se colapsa
        }}
      >
        {/* Rut Cliente */}
        <TextField
          label="Rut"
          value={user?.usuario || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: { shrink: true },
          }}
          sx={{
            minWidth: 140,
            flexShrink: 0,
            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
              textAlign: 'center',
              letterSpacing: '0.03em',
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        {/* Nombre Cliente */}
        <TextField
          label="Cliente"
          value={user?.nombre || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            flexShrink: 0,

            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        {/* Selector enrolado */}
        <TextField
          select
          label="Enrolado"
          disabled={scraperOcupado}
          value={rutSeleccionado}
          size="small"
          onChange={(e) => setRutSeleccionado(e.target.value)}
          sx={{
            minWidth: 260,
            flexGrow: 1,
          }}
        >
          {enrolados.length > 0 ? (
            enrolados.map((item) => (
              <MenuItem key={item.rut_enr} value={item.rut_enr}>
                {item.nombre_enr || item.rut_enr}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>No hay enrolados</MenuItem>
          )}
        </TextField>

        {/* Mes */}
        <TextField
          id="mes-input"
          select
          label="Mes"
          disabled={scraperOcupado}
          value={mes}
          onChange={handleMesChange}
          variant="outlined"
          required
          sx={{ width: 90,
            height: '40px', // Altura del TextField
            '& .MuiOutlinedInput-root': { height: '40px' },
            '& .MuiSelect-select': {
              textAlign: 'center',
            }
          }}
        >
          {Array.from({ length: 12 }, (_, i) => (
            <MenuItem key={i + 1} value={i + 1}>
              {i + 1}
              </MenuItem>
          ))}
        </TextField>

        {/* Año */}
        <TextField
          id="ano-input"
          select
          label="Año"
          disabled={scraperOcupado}
          value={ano}
          onChange={handleAnoChange}
          variant="outlined"
          required
          sx={{ width: 90,
            height: '40px', // Altura del TextField
            '& .MuiOutlinedInput-root': { height: '40px' },
            '& .MuiSelect-select': {
              textAlign: 'center',
            }
          }}
        >
          {/* Genera dinámicamente los años desde el año actual hasta 6 años atrás */}
          {years.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </TextField>

        {/* Boton Solicitar */}
        <Button
          variant="contained"
          startIcon={<RiFileSearchLine />}
          onClick={() => {
            if (scraperOcupado) return;
            setPage(0);
            setRowCount(0);
            solicitarExtraccion();
          }}
          sx={{
            minWidth: 110,
            height: 40,
            flexShrink: 0,

            backgroundColor: (theme) => theme.custom.ui.primaryAction,
            color: (theme) => theme.custom.ui.primaryActionText,

            '&:hover': {
              backgroundColor: (theme) => theme.custom.ui.primaryActionHover,
            },
          }}
        >
          {scraperOcupado ? "Procesando..." : "Solicitar"}
        </Button>

      </Stack>

      {/* Progreso Async */}
      {(jobId || progresoTexto) && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {progresoTexto || "⏳ Ejecutando extracción..."}
          </Typography>

          {progress?.current && progress?.total && (
            <LinearProgress
              variant="determinate"
              value={(progress.current / progress.total) * 100}
              sx={{ mt: 0.5 }}
            />
          )}
        </Box>
      )}


      {/* DataGrid */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 210px)', // ajusta según tu layout
          minHeight: 0,
        }}
      >
        <DataGrid
          rows={bitacora}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          rowHeight={38}
          headerHeight={40}
          disableRowSelectionOnClick

          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
            const isPageSizeChange = newPageSize !== pageSize;

            const finalPage = isPageSizeChange ? 0 : newPage;

            setPage(finalPage);
            setPageSize(newPageSize);
          }}

          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: DataGridSkeleton,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 400 },
            },
          }}
          sx={{
            height: '100%',
            width: '100%',
            minWidth: 720,      // scroll horizontal cuando corresponde
            border: 'none',
            // accesibilidad / foco
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString('es-CL')} registros encontrados
      </Typography>

    </Box>
  );
};

export default Extraerlibrocomprasenr;
